// bet-history.ts — what the user has ACTUALLY bet, before we ever showed up.
//
// Measured live on parikara.com 2026-08-08. Two REST endpoints on Digitain's host,
// issued by the PARENT document with a plain `fetch` and an Authorization header —
// not through the RequestHelper iframe, unlike everything else on this integration.
// So our overlay, which lives in that same window, can read them by wrapping fetch.
//
//   GET …/AccountService/GetOrdersV2?languageId&dateFrom&dateTo&orderType&statusId&take
//        → msgpack `[status, orders]`. Each order is a POSITIONAL array of 62 fields
//          with no key names. We read exactly ONE of them: index 0, the order number.
//          Anything else read by index would silently change meaning the day Digitain
//          inserts a field, and no test of ours could notice.
//
//   GET …/AccountService/GetBetHistoryItemDetails?languageId&orderNumber&orderType
//        → msgpack MAP with real keys: `CB.{TO,BD,T,BA,CSN,ST,OBS[]}`, and each leg in
//          `OBS` carries BOTH name pairs — `SN`/`ESN`, `TN`/`ETN`, `HT`/`EHT`, `AT`/`EAT`
//          — plus `F` (odds taken), `ST` (2 won / 3 lost), `STI`/`STN`/`STKN` (market and
//          selection), `EI`, `D`, `CN` (final score). This one is self-describing,
//          so the house rule applies unchanged: DISPLAY the localized name, MATCH the
//          English one.
//
// ── Request budget. This is somebody else's production sportsbook. ──────────────
// A naive backfill would fire one detail call per bet the moment the overlay boots —
// thirty requests in a burst, from a script the operator's WAF has never seen, on an
// endpoint that only ever gets hand-paced traffic. That is how you get an integration
// blocked, and the block would look like our widget being broken.
// So: passive first (their own calls cost us nothing), then at most a handful of
// spaced detail reads, once per week, and a hard stop the instant anything answers
// with a refusal.

import { decodeMsgPack } from "./msgpack";
import { oddsBand, normalizeSport } from "./event-dims";

const STORE_PREFIX = "bc_bets_v1:";
/** A taste profile ages slowly. Refresh weekly, keep serving in between. */
const REFRESH_AFTER_MS = 7 * 24 * 60 * 60 * 1000;
/** Beyond this the profile is more archaeology than taste. */
const MAX_AGE_MS = 180 * 24 * 60 * 60 * 1000;

/** Hard ceiling on detail reads we initiate, per page load. 15, not 6: measured
 *  2026-08-09, Digitain's edge answered a 1-list + 6-detail run with no complaint,
 *  and at 2.5s spacing 15 is still under a minute of one-at-a-time traffic. A
 *  40-bet history then completes in three page loads instead of seven, which
 *  matters because a HALF-collected history is a biased profile — the newest bets
 *  first, which is the opposite of a taste. */
export const MAX_ACTIVE_DETAIL_FETCHES = 15;
/** Minimum gap between two of ours. Their own UI cannot go faster than a human tap. */
export const DETAIL_SPACING_MS = 2500;
/** Random extra delay on top, 0…this. The requests are indistinguishable from the
 *  user's own — same session, same token, same IP, same headers — with ONE
 *  exception: a fixed 2.5s cadence is a machine's, and behavioural filters read
 *  rhythm before volume. Jitter costs nothing and removes the only tell we have. */
export const DETAIL_JITTER_MS = 1500;
/** Wait this long after boot before the first one — never compete with page load. */
export const BACKFILL_DELAY_MS = 15000;
/** How many bets are worth profiling at all. */
export const MAX_ORDERS_TRACKED = 40;
/** How far back our OWN list request reaches. Their UI's default window is short
 *  (24h on the unsettled tab), which is why we ask for ourselves. */
export const HISTORY_WINDOW_DAYS = 365;
/** `statusId` for «Расчитанные» — settled. Read off their own request when the
 *  user opened that tab (2026-08-09); the other tabs use different values we have
 *  not catalogued and do not need. */
export const SETTLED_STATUS_ID = 21;
/** `take` for the list. THIRTY, because thirty is the number their own client
 *  sends — never a value we have not seen answered. The odds feed already taught
 *  this lesson on this integration: `count` above 80 comes back empty rather than
 *  erroring, so an unobserved parameter value fails silently and looks like "the
 *  user has no history". */
export const LIST_TAKE = 30;

const ORDERS_PATH = "AccountService/GetOrdersV2";
const DETAILS_PATH = "AccountService/GetBetHistoryItemDetails";

/** Leg statuses Digitain uses in `OBS[].ST` — verified on one won and one lost leg
 *  of the same acca. Everything else (void, cash-out, pending) stays unknown rather
 *  than being guessed into a win rate. */
const LEG_WON = 2;
const LEG_LOST = 3;

// ⚠️ NO in-play flag. `IsL` looks like "was this taken live" and is NOT: measured
// across the owner's FULL settled history (40 bets, 43 legs, 2026-08-09) it was
// false on every single one, including bets he confirms he placed in-play. On a
// settled bet it evidently means "the event is live NOW", which is never true.
// A field that is structurally always 0 is worse than a missing one — it invites
// exactly the conclusion I drew from it ("this person doesn't bet live"), which
// was wrong. If in-play share ever matters, find the field that actually carries it.
export interface BetLeg {
  sport: string;      // normalized slug from ESN
  league_en: string;  // ETN
  league: string;     // TN, display only
  home_en: string;
  away_en: string;
  /** The LOCALIZED pair (`HT`/`AT`). ⛔ Kept BESIDE the English one because the
   *  two sources disagree: the account payloads send English team names, and
   *  the LIVE board sends none at all — 97 live events on this board carried
   *  HT/AT and ZERO carried EHT/EAT (adapter.ts `mapApiEvent`). A team profile
   *  keyed only in English can therefore never match a live candidate on an RU
   *  or TK board. Leagues do not have this problem: `ECN` is always English. */
  home: string;
  away: string;
  odds: number | null;
  odds_band: string;
  market_id: number | null;   // STI
  /** The market's own name — `STN` («Исход»), in the language the history was
   *  fetched in. ⛔ NOT `STKN`, which is the SELECTION («П1»). This is the only
   *  place a market name reaches us at all: both board fan-outs ask Digitain for
   *  `stakeTypes: [1, 702]`, so a board can never name market #3. */
  market_name: string;        // STN
  selection: string;          // STKN
  won: boolean | null;
  event_id: string;
}

export interface BetRecord {
  order: string;
  placed_at: string;
  stake: number | null;
  currency: string;
  total_odds: number | null;
  legs: BetLeg[];
  /** All legs won → true; any leg lost → false; otherwise null (void, pending). */
  won: boolean | null;
}

/** The DERIVED profile — this, not the ledger, is what goes to the backend.
 *  Counts and rates answer every ranking question a raw bet list could, and a
 *  stake-by-stake record of somebody's gambling answers none of them better. */
export interface BetTasteProfile {
  bets: number;
  legs: number;
  /** slug → [times bet, times won] */
  sports: Record<string, [number, number]>;
  /** ENGLISH league name → [times bet, times won] */
  leagues: Record<string, [number, number]>;
  /** ENGLISH team name → [times bet, times won]. BOTH sides of every leg: a
   *  bet on a match is evidence of interest in that fixture, and which of the
   *  two the money was on is a different question from which teams this person
   *  follows. Derby aside, somebody betting Man City–Norwich every week is
   *  telling us about Man City whichever way they bet it. */
  teams: Record<string, [number, number]>;
  odds_bands: Record<string, [number, number]>;
  markets: Record<string, number>;
  coupon: { single: number; acca: number };
  /** Mean stake in the operator's own currency units, and that currency. */
  avg_stake: number | null;
  currency: string;
  updated_at: number;
}

interface Stored {
  bets: Record<string, BetRecord>;
  ts: number;
  /** Set when Digitain refused one of our reads. Persisted, so a refusal is not
   *  re-tested on every page load — that is the behaviour that turns one 429 into
   *  a pattern. */
  blockedUntil?: number;
  /** How many bets the last list call reported. The harvest is finished when we
   *  hold that many; until then the weekly cool-off is skipped so the remaining
   *  bets are picked up a few per page load instead of never. */
  harvestedThrough?: number;
}

const num = (v: unknown): number | null => {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};
const str = (v: unknown): string => (typeof v === "string" ? v : v == null ? "" : String(v));

/** Read order numbers out of the POSITIONAL list payload — index 0 only, and only
 *  when it looks like an order number. Digitain's own ids are 10-digit; a field
 *  shift would put a float, a string or a small enum here, and all of those are
 *  rejected rather than tracked. */
export function parseOrderNumbers(decoded: unknown): string[] {
  const envelope = Array.isArray(decoded) ? decoded : null;
  if (!envelope) return [];
  const rows = Array.isArray(envelope[1]) ? (envelope[1] as unknown[]) : null;
  if (!rows) return [];
  const out: string[] = [];
  for (const row of rows) {
    if (!Array.isArray(row) || row.length < 2) continue;
    const id = row[0];
    if (typeof id !== "number" || !Number.isInteger(id) || id < 1e6) continue;
    out.push(String(id));
    if (out.length >= MAX_ORDERS_TRACKED) break;
  }
  return out;
}

/** Details payload → one bet record. Returns null when the shape isn't what we
 *  measured, rather than half a record. */
export function parseBetDetails(decoded: unknown, order: string): BetRecord | null {
  if (!decoded || typeof decoded !== "object" || Array.isArray(decoded)) return null;
  const cb = (decoded as Record<string, unknown>).CB;
  if (!cb || typeof cb !== "object" || Array.isArray(cb)) return null;
  const c = cb as Record<string, unknown>;
  const obs = Array.isArray(c.OBS) ? (c.OBS as unknown[]) : [];
  if (!obs.length) return null;

  const legs: BetLeg[] = [];
  for (const raw of obs) {
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) continue;
    const l = raw as Record<string, unknown>;
    const odds = num(l.F);
    const st = num(l.ST);
    legs.push({
      // English ONLY. A slug built from the localized name («Теннис» → "теннис")
      // would be a profile key that can never match the board's "tennis" — a
      // silent no-op forever. Names below DO fall back, because both sides of a
      // name comparison come from this same feed in this same language.
      sport: l.ESN ? normalizeSport(str(l.ESN)) : "unknown",
      league_en: str(l.ETN) || str(l.TN),
      league: str(l.TN),
      home_en: str(l.EHT) || str(l.HT),
      away_en: str(l.EAT) || str(l.AT),
      home: str(l.HT) || str(l.EHT),
      away: str(l.AT) || str(l.EAT),
      odds,
      odds_band: oddsBand(odds),
      market_id: num(l.STI),
      market_name: str(l.STN),
      selection: str(l.STKN),
      won: st === LEG_WON ? true : st === LEG_LOST ? false : null,
      event_id: str(num(l.EI) ?? ""),
    });
  }
  if (!legs.length) return null;

  // Settled from the LEGS, not from a bet-level status code whose vocabulary we
  // have only partly observed. All won → won; any lost → lost; else unknown.
  let won: boolean | null = true;
  for (const leg of legs) {
    if (leg.won === false) { won = false; break; }
    if (leg.won === null) won = null;
  }

  return {
    order,
    placed_at: str(c.BD),
    stake: num(c.BA),
    currency: str(c.CSN),
    total_odds: num(c.TO),
    legs,
    won,
  };
}

/** Fold the records into the profile the backend actually receives. */
export function deriveProfile(bets: BetRecord[]): BetTasteProfile | null {
  if (!bets.length) return null;
  const sports: Record<string, [number, number]> = {};
  const leagues: Record<string, [number, number]> = {};
  const teams: Record<string, [number, number]> = {};
  const odds_bands: Record<string, [number, number]> = {};
  const markets: Record<string, number> = {};
  const bump = (m: Record<string, [number, number]>, k: string, won: boolean | null): void => {
    if (!k) return;
    const cur = m[k] || (m[k] = [0, 0]);
    cur[0] += 1;
    if (won) cur[1] += 1;
  };

  let legCount = 0, stakeSum = 0, stakeN = 0, single = 0, acca = 0;
  let currency = "";
  for (const b of bets) {
    (b.legs.length > 1 ? (acca += 1) : (single += 1));
    if (typeof b.stake === "number" && b.stake > 0) { stakeSum += b.stake; stakeN += 1; }
    if (!currency && b.currency) currency = b.currency;
    for (const leg of b.legs) {
      legCount += 1;
      bump(sports, leg.sport, leg.won);
      bump(leagues, leg.league_en, leg.won);
      // ⛔ BOTH SPELLINGS, because the two sources disagree — see BetLeg.home.
      // A team contributes to its English key AND its localized one, so the
      // lookup matches whichever spelling the board hands the backend. Keys are
      // deduped per leg so a board that sends one spelling counts once.
      for (const name of new Set([leg.home_en, leg.away_en, leg.home, leg.away])) {
        bump(teams, name, leg.won);
      }
      bump(odds_bands, leg.odds_band, leg.won);
      if (leg.market_id != null) markets[String(leg.market_id)] = (markets[String(leg.market_id)] || 0) + 1;
    }
  }

  return {
    bets: bets.length,
    legs: legCount,
    sports, leagues, teams, odds_bands, markets,
    coupon: { single, acca },
    avg_stake: stakeN ? Math.round((stakeSum / stakeN) * 100) / 100 : null,
    currency,
    updated_at: Date.now(),
  };
}

/**
 * Observes the operator's own bet-history traffic, and — within a strict budget —
 * fills the gaps itself.
 *
 * `enableBackfill` is off unless the caller passes it: passive mode adds ZERO
 * requests to the operator's site and is always safe to run.
 */
export class DigitainBetHistory {
  private key: string;
  private data: Stored = { bets: {}, ts: 0 };
  private started = false;
  private enableBackfill: boolean;
  /** The last history call the page made, as {url, headers}. The headers object is
   *  held opaquely and handed straight back to `fetch` — this code never reads,
   *  stores or transmits what is inside it.
   *
   *  ⚠️ BOTH call forms, and that is a measurement, not defensiveness: on Parikara
   *  (2026-08-09) the sportsbook calls `fetch(urlString, {headers})`, NOT
   *  `fetch(new Request(...))`. Recognising only the Request form left this
   *  permanently null, so the backfill could never run — while every unit test
   *  passed, because the test harness was the thing constructing the Request.
   *  Ask what PRODUCES the input, not only what the function does with it. */
  private lastCall: { url: string; headers: HeadersInit } | null = null;
  private fetched = 0;
  private backfillTimer: ReturnType<typeof setTimeout> | null = null;
  private observedThisSession = 0;
  private wrappedOriginal: typeof fetch | null = null;
  private wrappedFetch: typeof fetch | null = null;

  constructor(apiKey: string, enableBackfill = false) {
    this.key = STORE_PREFIX + (apiKey || "unknown");
    this.enableBackfill = enableBackfill;
  }

  start(): void {
    if (this.started) return;
    this.started = true;
    this.load();
    this.wrapFetch();
  }

  stop(): void {
    if (this.backfillTimer != null) clearTimeout(this.backfillTimer);
    this.backfillTimer = null;
    try {
      if (this.wrappedFetch && window.fetch === this.wrappedFetch && this.wrappedOriginal) {
        window.fetch = this.wrappedOriginal;
        const marked = window as unknown as { __bcBetsWrapped?: boolean };
        delete marked.__bcBetsWrapped;
      }
    } catch { /* no window */ }
    this.wrappedOriginal = null;
    this.wrappedFetch = null;
    this.started = false;
  }

  private wrapFetch(): void {
    try {
      const self = this;
      const original = window.fetch;
      if (typeof original !== "function") return;
      const marked = window as unknown as { __bcBetsWrapped?: boolean };
      if (marked.__bcBetsWrapped) return;
      marked.__bcBetsWrapped = true;
      const wrapped = function (this: unknown, ...args: Parameters<typeof fetch>) {
        const p = original.apply(this as never, args);
        try {
          const input = args[0] as Request | string | URL;
          const url = String((input as Request)?.url ?? input);
          if (url.indexOf(ORDERS_PATH) >= 0 || url.indexOf(DETAILS_PATH) >= 0) {
            const init = args[1] as RequestInit | undefined;
            const headers = (input as Request)?.headers ?? init?.headers;
            if (headers) self.lastCall = { url, headers };
            if (self.started) void self.observe(url, p);
          }
        } catch {
          /* never break the operator's own request */
        }
        return p;
      } as typeof fetch;
      this.wrappedOriginal = original;
      this.wrappedFetch = wrapped;
      window.fetch = wrapped;
    } catch {
      /* no window.fetch — passive mode simply yields nothing */
    }
  }

  private async observe(url: string, p: Promise<Response>): Promise<void> {
    try {
      if (!this.started) return;
      const res = await p;
      if (!this.started) return;
      if (!res.ok) return;
      const bytes = new Uint8Array(await res.clone().arrayBuffer());
      const decoded = decodeMsgPack(bytes);
      if (url.indexOf(DETAILS_PATH) >= 0) {
        const order = new URL(url, location.href).searchParams.get("orderNumber") || "";
        const rec = parseBetDetails(decoded, order);
        if (rec) this.put(rec);
      } else {
        // Their list is NOT what we harvest from — its window is whatever tab the
        // user happens to be on (24h on the unsettled one). Seeing it only tells us
        // the endpoint is live and that we now hold a call we can reuse; the
        // harvest asks for its own window.
        parseOrderNumbers(decoded);
        this.armBackfill();
      }
      if (this.started) this.observedThisSession += 1;
    } catch {
      /* a payload we cannot parse is simply not a payload we use */
    }
  }

  private put(rec: BetRecord): void {
    this.data.bets[rec.order] = rec;
    const orders = Object.keys(this.data.bets);
    if (orders.length > MAX_ORDERS_TRACKED) {
      // Drop the oldest by placement date, not by insertion order.
      orders
        .sort((a, b) => String(this.data.bets[a].placed_at).localeCompare(String(this.data.bets[b].placed_at)))
        .slice(0, orders.length - MAX_ORDERS_TRACKED)
        .forEach((o) => delete this.data.bets[o]);
    }
    this.data.ts = Date.now();
    this.save();
  }

  // ── budgeted backfill ─────────────────────────────────────────────────────
  private armBackfill(): void {
    if (!this.enableBackfill || this.backfillTimer != null) return;
    if (this.blocked()) return;
    // Weekly — UNLESS the harvest is unfinished. The profile is built from the
    // whole history, and the history arrives a few bets per page load; refusing to
    // continue because "we looked this week" would freeze it at whatever the first
    // load happened to reach.
    const haveAll = this.data.harvestedThrough != null
      && Object.keys(this.data.bets).length >= this.data.harvestedThrough;
    if (haveAll && Date.now() - this.data.ts < REFRESH_AFTER_MS) return;
    this.backfillTimer = setTimeout(() => void this.runBackfill(), BACKFILL_DELAY_MS);
  }

  /**
   * Pull OUR OWN order list over a wide window, then read details for the bets we
   * are still missing.
   *
   * The list call is the point. Overhearing theirs sounds cheaper and is nearly
   * useless: their `dateFrom` defaults to the last 24 HOURS, so on an account with
   * a year of betting we kept "discovering" the one bet placed yesterday — and a
   * one-leg profile never clears `bet_taste.MIN_LEGS`, so the whole feature would
   * have sat there doing nothing while looking healthy. One extra request buys the
   * entire history.
   *
   * Strictly serial, spaced, capped per page load, resumable on the next one, and
   * it gives up for a day on the first refusal.
   */
  private async runBackfill(): Promise<void> {
    if (!this.started) return;
    const orders = await this.fetchOrderList();
    if (!orders) return;
    this.data.harvestedThrough = orders.length;
    this.save();
    const missing = orders.filter((o) => !this.data.bets[o]).slice(0, MAX_ACTIVE_DETAIL_FETCHES);
    if (!missing.length) return;
    await this.fetchDetails(missing);
  }

  /** One list request over `HISTORY_WINDOW_DAYS`, reusing the page's own call as
   *  the template. Returns null if we cannot ask or were refused. */
  private async fetchOrderList(): Promise<string[] | null> {
    const call = this.lastCall;
    if (!call) return null; // no authenticated call seen — passive only, by design
    try {
      const base = new URL(call.url);
      const u = new URL(base.origin + base.pathname.replace(/GetBetHistoryItemDetails$/, "GetOrdersV2"));
      for (const [k, v] of base.searchParams) u.searchParams.set(k, v);
      u.searchParams.set("dateFrom", new Date(Date.now() - HISTORY_WINDOW_DAYS * 864e5).toISOString());
      u.searchParams.set("dateTo", "");
      u.searchParams.set("take", String(LIST_TAKE));
      // SETTLED only, and not whatever tab the user was on. Inheriting their
      // `statusId` meant that arriving from «Не Расчитанные» asked for unsettled
      // bets over a year — measured 2026-08-09, that returns nothing, and the
      // harvest quietly did one request and stopped. Settled is also the right
      // set on the merits: an open bet has no outcome, so it teaches the profile
      // what this person BACKS but nothing about what came off.
      u.searchParams.set("statusId", String(SETTLED_STATUS_ID));
      u.searchParams.set("orderType", "0");
      const res = await fetch(u.toString(), { method: "GET", headers: call.headers });
      this.fetched += 1;
      if (this.refused(res)) return null;
      if (!res.ok) return null;
      return parseOrderNumbers(decodeMsgPack(new Uint8Array(await res.arrayBuffer())));
    } catch {
      return null;
    }
  }

  private async fetchDetails(orders: string[]): Promise<void> {
    if (!this.started) return;
    const call = this.lastCall;
    if (!call) return;
    for (const order of orders) {
      if (!this.started) return;
      if (this.fetched > MAX_ACTIVE_DETAIL_FETCHES || this.blocked()) return;
      try {
        const base = new URL(call.url);
        const u = new URL(base.origin + base.pathname);
        u.pathname = u.pathname.replace(/GetOrdersV2$/, "GetBetHistoryItemDetails");
        u.searchParams.set("languageId", base.searchParams.get("languageId") || "1");
        u.searchParams.set("orderNumber", order);
        u.searchParams.set("orderType", "0");
        const res = await fetch(u.toString(), { method: "GET", headers: call.headers });
        this.fetched += 1;
        if (this.refused(res)) return;
        if (!res.ok) return;
        const rec = parseBetDetails(decodeMsgPack(new Uint8Array(await res.arrayBuffer())), order);
        if (rec) this.put(rec);
      } catch {
        return; // network error — stop, don't hammer
      }
      await new Promise((r) =>
        setTimeout(() => { if (this.started) r(undefined); }, DETAIL_SPACING_MS + Math.random() * DETAIL_JITTER_MS));
    }
  }

  /** Did Digitain's edge say no? One refusal ends the harvest for a day, and
   *  across reloads — retrying a WAF is how a soft block becomes a hard one. */
  private refused(res: Response): boolean {
    if (res.status === 401 || res.status === 403 || res.status === 429 || res.status >= 500) {
      this.data.blockedUntil = Date.now() + 24 * 60 * 60 * 1000;
      this.save();
      return true;
    }
    return false;
  }

  private blocked(): boolean {
    return !!this.data.blockedUntil && Date.now() < this.data.blockedUntil;
  }

  // ── storage ───────────────────────────────────────────────────────────────
  private load(): void {
    try {
      const raw = localStorage.getItem(this.key);
      if (!raw) return;
      const parsed = JSON.parse(raw) as Stored;
      if (parsed && typeof parsed === "object" && parsed.bets) this.data = parsed;
    } catch {
      this.data = { bets: {}, ts: 0 };
    }
  }

  private save(): void {
    try {
      localStorage.setItem(this.key, JSON.stringify(this.data));
    } catch {
      /* private mode / quota */
    }
  }

  /** SETTLED legs, as one flat record per leg, for the backend's event view.
   *
   * ⛔ This EXTENDS what leaves the page, so it is deliberately the narrowest
   * thing that answers the two questions asked of it — did the bet win, and
   * what was the match called:
   *
   *   * NO stake, and no per-bet total. Money is already reported on the
   *     placement receipt for bets we saw placed; re-sending it from a history
   *     that also holds bets placed before we existed would widen the ledger,
   *     not complete it.
   *   * `order` IS sent, because we already store it on the receipt — it is the
   *     join that turns "this bet won" into "the pick we recommended won",
   *     which is the whole point.
   *   * The English names ride along because this history is the only place
   *     Digitain sends them for an arbitrary past event, and a fixture nobody
   *     can name is a fixture nobody can sharpen advice for.
   *
   * `won: null` legs (void, pending) are DROPPED: a settlement record that says
   * nothing about the outcome is not a settlement.
   */
  settlements(): Array<Record<string, unknown>> {
    const out: Array<Record<string, unknown>> = [];
    for (const bet of Object.values(this.data.bets)) {
      for (const leg of bet.legs) {
        if (leg.won === null || !leg.event_id) continue;
        out.push({
          order: bet.order,
          event_id: leg.event_id,
          won: leg.won,
          odds: leg.odds,
          market_id: leg.market_id,
          // ⭐ The market in the book's OWN words, so the backend can turn every
          // `#3` on the event-demand page into a label. It rides on a settlement
          // and nowhere else — see BetLeg.market_name.
          market_name: leg.market_name,
          selection: leg.selection,
          sport_en: leg.sport,
          league_en: leg.league_en,
          home_en: leg.home_en,
          away_en: leg.away_en,
        });
      }
    }
    return out;
  }

  /** The derived profile for /suggestions, or null when we know nothing. */
  profile(): BetTasteProfile | null {
    if (Date.now() - this.data.ts > MAX_AGE_MS) return null;
    return deriveProfile(Object.values(this.data.bets));
  }

  /** One line for the `?bcdiag=1` strip. */
  diagLine(): string {
    const p = this.profile();
    // The budget belongs in BOTH branches. It was only in the second, so during the
    // one reading that mattered — profile still empty, is the backfill even firing? —
    // the strip said "none" and nothing else, and the answer took a console to get.
    const budget = `fetched=${this.fetched}/${MAX_ACTIVE_DETAIL_FETCHES}`;
    const armed = !this.enableBackfill ? "off" : this.lastCall ? "armed" : "no-auth-seen";
    if (!p) {
      return `none (seen=${this.observedThisSession} ${budget} ${armed}`
        + `${this.blocked() ? " BLOCKED" : ""})`;
    }
    const top = Object.entries(p.sports).sort((a, b) => b[1][0] - a[1][0])[0];
    const age = Math.round((Date.now() - p.updated_at) / 60000);
    // `bets=7/23` — how much of the history we hold against how much exists. A bare
    // count cannot say whether the harvest is done or stalled.
    const outOf = this.data.harvestedThrough != null ? `/${this.data.harvestedThrough}` : "";
    return `bets=${p.bets}${outOf} legs=${p.legs} top=${top ? top[0] : "?"} `
      + `${budget} ${age}m`;
  }
}
